import styled, { keyframes } from "styled-components";

const fadeIn = keyframes` from { opacity: 0; } to { opacity: 1; } `;
const slideIn = keyframes` from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } `;
const pulse = keyframes` 0% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4); } 70% { box-shadow: 0 0 0 15px rgba(212, 175, 55, 0); } 100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0); } `;
const float = keyframes` 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } `;
const twinkle = keyframes` 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } `;

export const WarningOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 8, 20, 0.95);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 11000;
  padding: 15px;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

export const WarningContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fffef5 50%, #fdfbf0 100%);
  border-radius: 24px;
  border: 2px solid #d4af37;
  padding: 50px 40px 40px 40px;
  box-sizing: border-box;
  max-width: 540px;
  width: 100%;
  margin: auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 20px 60px rgba(212, 175, 55, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.5);
  animation: ${slideIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;

  @media (max-width: 480px) {
    padding: 40px 20px 30px 20px;
    border-radius: 20px;
  }

  @media (max-height: 760px) {
    padding: 35px 30px 25px 30px;
  }

  @media (max-height: 600px) {
    padding: 28px 24px 20px 24px;
    border-radius: 16px;
  }
`;

export const TopDecoration = styled.div`
  position: absolute;
  top: 15px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 20px;
  pointer-events: none;

  .star {
    color: #d4af37;
    font-size: 20px;
    animation: ${twinkle} 1.5s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.3s;
    }

    &:nth-child(3) {
      animation-delay: 0.6s;
    }
  }

  @media (max-width: 480px) {
    gap: 15px;

    .star {
      font-size: 16px;
    }
  }
`;

export const IconBox = styled.div`
  font-size: 60px;
  color: #d4af37;
  margin-bottom: 20px;
  animation: ${float} 3s ease-in-out infinite;

  @media (max-width: 480px) {
    font-size: 48px;
    margin-bottom: 15px;
  }

  @media (max-height: 760px) {
    font-size: 44px;
    margin-bottom: 10px;
  }

  @media (max-height: 600px) {
    font-size: 36px;
    margin-bottom: 6px;
  }
`;

export const WarningHeader = styled.div`
  margin-bottom: 30px;

  @media (max-height: 760px) {
    margin-bottom: 18px;
  }

  @media (max-height: 600px) {
    margin-bottom: 12px;
  }
`;

export const Badge = styled.span`
  background: ${(props) =>
    props.variant === "premium"
      ? "linear-gradient(135deg, #d4af37 0%, #f0c856 100%)"
      : "#d4af37"};
  color: white;
  padding: 6px 16px;
  box-sizing: border-box;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.5px;
  margin-bottom: 15px;
  display: inline-block;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
`;

export const WarningTitle = styled.h2`
  margin: 0;
  font-size: 32px;
  font-weight: 900;
  color: #1a1a1a;
  line-height: 1.3;
  letter-spacing: -0.5px;

  @media (max-width: 480px) {
    font-size: 24px;
  }

  @media (max-height: 760px) {
    font-size: 24px;
    line-height: 1.25;
  }

  @media (max-height: 600px) {
    font-size: 20px;
  }
`;

export const Highlight = styled.span`
  color: #d4af37;
  background: linear-gradient(120deg, #ffd700, #b8860b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
`;

export const WarningTextContent = styled.div`
  margin-bottom: 35px;
  width: 100%;

  .main-text {
    font-size: 16px;
    line-height: 1.6;
    color: #2d3748;
    margin: 0 0 20px 0;
    font-weight: 600;

    strong {
      color: #d4af37;
      font-weight: 800;
    }
  }

  .footer-text {
    font-size: 14px;
    margin-top: 20px;
    color: #718096;
    line-height: 1.5;

    strong {
      color: #1a1a1a;
      font-weight: 700;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 25px;

    .main-text {
      font-size: 15px;
    }
  }

  @media (max-height: 760px) {
    margin-bottom: 18px;

    .main-text {
      font-size: 14px;
      margin: 0 0 12px 0;
    }

    .footer-text {
      font-size: 13px;
      margin-top: 12px;
    }
  }

  @media (max-height: 600px) {
    margin-bottom: 12px;

    .footer-text {
      display: none;
    }
  }
`;

export const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 25px 0;
  text-align: left;

  @media (max-height: 760px) {
    gap: 8px;
    margin: 14px 0;
  }

  @media (max-height: 600px) {
    gap: 6px;
    margin: 10px 0;
  }
`;

export const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(212, 175, 55, 0.08);
  border-left: 3px solid #d4af37;
  font-size: 15px;
  color: #2d3748;
  line-height: 1.5;

  svg {
    color: #d4af37;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    color: #1a1a1a;
    font-weight: 700;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px;
    gap: 10px;

    svg {
      font-size: 16px;
    }
  }

  @media (max-height: 760px) {
    font-size: 13px;
    padding: 8px 10px;
    line-height: 1.35;
  }

  @media (max-height: 600px) {
    font-size: 12px;
    padding: 6px 10px;
    border-left-width: 2px;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (max-height: 600px) {
    gap: 8px;
  }
`;

export const AcceptButton = styled.button`
  background: linear-gradient(135deg, #d4af37 0%, #b8860b 100%);
  color: white;
  padding: 18px 30px;
  box-sizing: border-box;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${pulse} 2s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 20px rgba(212, 175, 55, 0.25);

  &:hover {
    transform: translateY(-3px);
    filter: brightness(1.15);
    box-shadow: 0 12px 30px rgba(212, 175, 55, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 16px 20px;
    font-size: 15px;
  }

  @media (max-height: 760px) {
    padding: 14px 20px;
    font-size: 14px;
  }

  @media (max-height: 600px) {
    padding: 11px 16px;
    font-size: 13px;
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  color: #718096;
  border: 1px solid #e2e8f0;
  padding: 12px 20px;
  box-sizing: border-box;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #2d3748;
    border-color: #cbd5e0;
    background: rgba(0, 0, 0, 0.02);
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 13px;
  }

  @media (max-height: 600px) {
    padding: 8px 14px;
    font-size: 12px;
  }
`;
